import {Component, OnInit} from '@angular/core';
import {User} from '@models/user';
import {EMPTY_USER} from '@models/empty';
import {CommonService} from '@services/common.service';
import {FormService} from '@services/form.service';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {AuthenticationService} from '@services/authentification.service';
import {UserService} from '@services/user.service';
import {environment} from '~/environments/environment';
import {HttpClient} from '@angular/common/http';
import {TrackingService} from '@services/tracking.service';
import {ActivatedRoute} from '@angular/router';
import {Invoice} from '@models/invoice';
import {AlertService} from '@services/alert.service';
import {MatDialog} from '@angular/material/dialog';
import {DeleteUserComponent} from '@module/users/popup/delete-user/delete-user.component';
import {PayInvoiceComponent} from '@module/users/popup/pay-invoice/pay-invoice.component';

declare global {
    interface Window {
        WonderPush: any;
    }
}

@Component({
    selector: 'app-account',
    templateUrl: './account.component.html',
    styleUrls: ['./account.component.scss']
})
export class AccountComponent implements OnInit {
    accountForm: UntypedFormGroup;
    passwordForm: UntypedFormGroup;
    user: User = Object.assign({}, EMPTY_USER);
    stripe = Stripe(environment.stripeKey);
    hide = true;
    invoices = [];
    filleuls = [];
    editPaiement = '';
    totalComm = 0;
    // Affiliation (nouveau format GET /app/user/filleul)
    filleulLoading = true;
    isInfluenceur = false;
    affiliationActive = true;   // programme d'affiliation actif
    affiliationRate = 0;        // taux de commission (%)
    affiliationTotal = 0;       // total gagné (centimes)
    affiliationRemaining = 0;   // restant dû (centimes)
    affiliationActiveUsers = 0;
    affiliationRegisteredLast30 = 0;
    paiements = [];
    showAllPaiements = false; // par défaut : uniquement les commissions depuis le dernier virement

    constructor(
        private formBuilder: UntypedFormBuilder,
        public commonService: CommonService,
        public formService: FormService,
        public userService: UserService,
        public alertService: AlertService,
        private route: ActivatedRoute,
        public authenticationService: AuthenticationService,
        public trackingService: TrackingService,
        public dialog: MatDialog,
        private http: HttpClient) {
    }

    ngOnInit(): void {
        this.editPaiement = this.route.snapshot.queryParams.editPaiement || 'false';
        if (this.editPaiement === 'true') {
            this.updateCB();
        }

        this.accountForm = this.formBuilder.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            plan: [''],
            email: [{value: '', disabled: true}, [Validators.required, Validators.email]],
            mailing: [''],
            adress: [''],
            city: [''],
            zip_code: [''],
            company_name: [''],
            mobile: [],
        });

        this.passwordForm = this.formBuilder.group({
            old_password: ['', [Validators.minLength(8)]],
            new_password: ['', [Validators.required, Validators.minLength(8)]],
            new_password2: ['', [Validators.required, Validators.minLength(8)]]
        }, {validator: this.matchingPasswords('new_password', 'new_password2')});

        this.user = this.authenticationService.currentUser;

        let data = {
            mobile: "",
        }
        if (this.user.pro_data !== "") {
            data = JSON.parse(this.user.pro_data);
        }
        this.accountForm = this.formBuilder.group({
            firstName: [this.user.firstname, Validators.required],
            lastName: [this.user.lastname, Validators.required],
            plan: [this.user.plan],
            email: [{value: this.user.email, disabled: true}, [Validators.required, Validators.email]],
            mailing: [this.user.mailing],
            adress: [this.user.adress],
            city: [this.user.city],
            zip_code: [this.user.zip_code],
            company_name: [this.user.company_name],
            mobile: [data.mobile === undefined ? '' : data.mobile],
        });

        this.userService.getInvoice().subscribe(invoices => {
            this.invoices = invoices;
        }, error => {
        });

        this.loadFilleul();

        if (window.WonderPush !== undefined) {
            window.WonderPush.push(function() {
                window.WonderPush.showSubscriptionSwitch(
                    document.getElementById('wonderpush-subscription-switch'),
                    {
                        sentence: "Activer les notifications sur cet appareil : ",
                        colorOn: "#0075DB",
                    }
                );
            });
        }

        this.onChanges();
    }

    matchingPasswords(passwordKey: string, confirmPasswordKey: string) {
        return (group: UntypedFormGroup): { [key: string]: any } => {
            const password = group.controls[passwordKey];
            const confirmPassword = group.controls[confirmPasswordKey];

            if (password.value !== confirmPassword.value) {
                return {
                    mismatchedPasswords: true
                };
            }
        };
    }

    onChanges(): void {
        this.accountForm.valueChanges.subscribe(val => {
            if (this.accountForm.invalid) {
                return;
            }

            const form = this.accountForm.getRawValue();
            Object.keys(form).forEach((k) => {
                this.user[k] = form[k];
            });
            this.user.pro_data = JSON.stringify({mobile: form['mobile']});
            this.userService.updateAccount(this.user);
        });
    }

    updateCB() {
        this.http.post<any>(`${environment.apiUrl}app/paiement/create-update-checkout-session`, {}).subscribe(
            session => {

                this.stripe.redirectToCheckout({sessionId: session.id}).then(result => {
                    if (result.error) {
                        alert(result.error.message);
                    }
                });
            },
            (error) => {
                console.log(error);
            });
    }

    passwordUpdate(): void {
        if (this.passwordForm.invalid) {
            return;
        }
        this.userService.updatePassword(this.passwordForm.getRawValue());
    }

    loadFilleul() {
        this.filleulLoading = true;
        this.userService.getFilleul().subscribe(res => {
            this.isInfluenceur = res.is_influenceur;
            if (res.is_influenceur) {
                this.affiliationActive = res.active !== false;
                this.affiliationRate = res.rate || 0;
                this.affiliationTotal = res.total || 0;
                this.affiliationRemaining = res.remaining || 0;
                this.affiliationActiveUsers = res.active_users || 0;
                this.affiliationRegisteredLast30 = res.registered_last_30 || 0;
                this.paiements = res.paiements || [];
                this.totalComm = this.affiliationTotal / 100; // centimes -> euros
            } else {
                this.filleuls = res.filleuls || [];
            }
            this.filleulLoading = false;
        }, error => {
            this.filleulLoading = false;
        });
    }

    // Solde suffisant pour une demande de retrait (min. 200 € = 20000 centimes).
    get canWithdraw(): boolean {
        return this.affiliationRemaining >= 20000;
    }

    // Ouvre la popup de dépôt de facture (explications + upload + validation).
    openInvoiceDialog() {
        const ref = this.dialog.open(PayInvoiceComponent, {
            width: '520px',
            data: {remaining: this.affiliationRemaining},
        });
        ref.afterClosed().subscribe(res => {
            if (res && res.success) {
                this.alertService.success('Demande de retrait enregistrée (' + (res.amount / 100).toFixed(2) + ' € HT). Règlement sous 30 jours.');
                this.loadFilleul();
            }
        });
    }

    // Index du dernier virement (type 'paiement') dans la liste (triée du plus récent au plus ancien).
    private get lastPaiementIndex(): number {
        return this.paiements.findIndex(p => p.type === 'paiement');
    }

    // Mouvements affichés : depuis le dernier virement (ligne de virement incluse) par défaut, ou tout.
    get displayedPaiements(): any[] {
        if (this.showAllPaiements) {
            return this.paiements;
        }
        const idx = this.lastPaiementIndex;
        return idx === -1 ? this.paiements : this.paiements.slice(0, idx + 1);
    }

    // Y a-t-il des mouvements plus anciens à révéler ?
    get hasMorePaiements(): boolean {
        return !this.showAllPaiements && this.displayedPaiements.length < this.paiements.length;
    }

    openInvoice(invoice: Invoice): void {
        this.userService.getInvoiceLink(invoice).subscribe(
            result => {
                if (!result.url) {
                    this.alertService.error('Une erreur est survenue, veuillez nous contacter.');
                } else {
                    window.location.href = result.url;
                }
            },
            (error) => {
                console.log(error);
            });
    }

    deleteAccount() {
        const dialogRef = this.dialog.open(DeleteUserComponent, {
            width: '440px',
            height: '400px',
        });
    }
}
